import { useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { trackEvent } from '../../analytics/events.js';
import { AuthShowcase } from '../../components/AuthShowcase.js';
import { AuthLayout, Field, FormError } from '../../components/Form.js';
import { LegalLinks } from '../../components/LegalLinks.js';
import { ApiError, apiRequest } from '../../lib/api.js';
import { GoogleButton } from './GoogleButton.js';

/** Story #2 — self-serve signup. Cognito emails a verification code. */
export function SignUp() {
  const navigate = useNavigate();
  // Forwarded, not read: the channel was decided by the page that linked here,
  // and `/verify` is where it finally reaches the API.
  const [params] = useSearchParams();
  const via = params.get('via');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Whether this form has already reported that someone started filling it in.
   *
   * A ref rather than state: it must not re-render the form on the first
   * keystroke, and the value is never read during render.
   *
   * The event it guards is the one number nothing else in this system can
   * produce. The API hears about accounts that were created; the person who
   * typed a name and an email and then closed the tab is invisible to it, and
   * that gap is what the signup form's real conversion rate is made of.
   */
  const startedTracked = useRef(false);

  function onFirstInput() {
    if (startedTracked.current) return;
    startedTracked.current = true;
    trackEvent({ name: 'signup_start' });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await apiRequest('/auth/signup', {
        method: 'POST',
        body: { name, email, password },
        anonymous: true,
      });
      const next = `/verify?email=${encodeURIComponent(email)}`;
      navigate(via ? `${next}&via=${encodeURIComponent(via)}` : next);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create your account.');
      // The reason, never the message. `ApiError.message` is written for the
      // person reading it and can quote what they typed, so sending it would
      // put an email address in Google's logs under the name of a failure
      // reason. Said no, or was never reached, is the distinction worth having.
      trackEvent({
        name: 'signup_failed',
        reason: caught instanceof ApiError ? 'rejected' : 'unreachable',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      // No subtitle: the showcase alongside makes the pitch, and repeating it
      // two inches away only weakens both.
      aside={<AuthShowcase />}
      footer={
        <>
          <p className="muted">
            Already have an account? <Link to="/signin">Sign in</Link>
          </p>
          <LegalLinks />
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Field label="Your name" htmlFor="name">
          <input
            id="name"
            value={name}
            onChange={(e) => {
              onFirstInput();
              setName(e.target.value);
            }}
            autoComplete="name"
            required
          />
        </Field>

        <Field label="Email" htmlFor="email">
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => {
              onFirstInput();
              setEmail(e.target.value);
            }}
            autoComplete="email"
            required
          />
        </Field>

        <Field
          label="Password"
          htmlFor="password"
          hint="At least 10 characters, with upper case, lower case and a number."
        >
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => {
              onFirstInput();
              setPassword(e.target.value);
            }}
            autoComplete="new-password"
            aria-describedby="password-hint"
            required
          />
        </Field>

        <FormError message={error} />

        <button type="submit" disabled={busy} className="block">
          {busy ? 'Creating your account…' : 'Create account'}
        </button>

        <p className="field-hint">
          By creating an account you agree to our <Link to="/terms">Terms of Service</Link> and{' '}
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </form>

      <GoogleButton label="Sign up with Google" />
    </AuthLayout>
  );
}
